import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import {
  approveMpesaCheckoutManually,
  fetchMpesaCheckoutStatus,
  startMpesaCheckout,
  type MpesaCheckoutStatus,
  type OrderItem,
} from '../../lib/api';

const POLL_INTERVAL_MS = 10000;
const MAX_POLL_DURATION_MS = 60000;
const STATUS_REFRESH_POLLS = new Set([5]);

export default function CheckoutPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { cart = [], total = 0, customerName: initialCustomerName = '', customerPhone: initialCustomerPhone = '' } = (location.state || {}) as {
    cart?: OrderItem[];
    total?: number;
    customerName?: string;
    customerPhone?: string;
  };

  const [customerName, setCustomerName] = useState(initialCustomerName);
  const [customerPhone, setCustomerPhone] = useState(initialCustomerPhone);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [isManuallyConfirming, setIsManuallyConfirming] = useState(false);
  const [checkoutRequestId, setCheckoutRequestId] = useState<string | null>(null);
  const [pendingOrderId, setPendingOrderId] = useState<number | null>(null);
  const [successOrderNumber, setSuccessOrderNumber] = useState('');
  const [customerMessage, setCustomerMessage] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [receiptNumber, setReceiptNumber] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollCountRef = useRef(0);
  const pollStartedAtRef = useRef(0);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (!checkoutRequestId) {
      return undefined;
    }

    let cancelled = false;
    setIsPolling(true);

    const poll = async () => {
      const elapsed = Date.now() - pollStartedAtRef.current;

      if (elapsed >= MAX_POLL_DURATION_MS) {
        setIsPolling(false);
        setCheckoutRequestId(null);
        setError(
          'M-Pesa confirmation timed out after 60 seconds. Check the transaction on the phone, then confirm from the live orders screen.'
        );
        return;
      }

      try {
        const status = await fetchMpesaCheckoutStatus(
          checkoutRequestId,
          STATUS_REFRESH_POLLS.has(pollCountRef.current)
        );

        if (cancelled) {
          return;
        }

        handleStatus(status);
        pollCountRef.current += 1;

        if (status.status === 'processing' || status.status === 'pending') {
          timeoutRef.current = window.setTimeout(poll, POLL_INTERVAL_MS);
          return;
        }

        setIsPolling(false);
      } catch (pollError) {
        if (cancelled) {
          return;
        }

        setIsPolling(false);
        setError(pollError instanceof Error ? pollError.message : 'Unable to confirm payment status.');
      }
    };

    timeoutRef.current = window.setTimeout(poll, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;

      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, [checkoutRequestId]);

  const handleStatus = (status: MpesaCheckoutStatus) => {
    setSuccessOrderNumber(status.orderNumber);
    setStatusMessage(status.resultDescription ?? status.refreshError ?? 'Awaiting M-Pesa confirmation.');
    setReceiptNumber(status.mpesaReceipt);

    if (status.status === 'succeeded') {
      setShowSuccess(true);
      setCheckoutRequestId(null);
      setPendingOrderId(null);
      window.setTimeout(() => {
        navigate('/live-orders');
      }, 2500);
      return;
    }

    if (status.status === 'failed') {
      setCheckoutRequestId(null);
      setPendingOrderId(null);
      setError(status.resultDescription ?? 'The M-Pesa payment was not completed.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!customerName || !customerPhone) {
      setError('Please fill in all fields.');
      return;
    }

    try {
      setError(null);
      setShowSuccess(false);
      setReceiptNumber(null);
      setStatusMessage(null);
      setCustomerMessage(null);
      setIsProcessing(true);
      pollCountRef.current = 0;
      pollStartedAtRef.current = Date.now();

      const response = await startMpesaCheckout({
        customerName,
        customerPhone,
        paymentMethod: 'mpesa',
        cart,
      });

      setPendingOrderId(response.order.id);
      setSuccessOrderNumber(response.order.orderNumber);
      setCustomerMessage(response.customerMessage);
      setCheckoutRequestId(response.checkoutRequestId);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Unable to start M-Pesa checkout.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleManualConfirmation = async () => {
    if (pendingOrderId === null) {
      setError('No pending payment was found to approve manually.');
      return;
    }

    try {
      setError(null);
      setIsManuallyConfirming(true);

      const response = await approveMpesaCheckoutManually(pendingOrderId);

      setCheckoutRequestId(null);
      setPendingOrderId(null);
      setStatusMessage('Payment approved manually. Redirecting to live orders...');
      setShowSuccess(true);
      setSuccessOrderNumber(response.order.orderNumber);

      window.setTimeout(() => {
        navigate('/live-orders');
      }, 1500);
    } catch (manualError) {
      setError(manualError instanceof Error ? manualError.message : 'Unable to approve payment manually.');
    } finally {
      setIsManuallyConfirming(false);
    }
  };

  if (cart.length === 0 && !showSuccess) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 py-12 text-center">
          <i className="ri-shopping-cart-line text-6xl text-gray-300 w-20 h-20 flex items-center justify-center mx-auto mb-4"></i>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">No items to checkout</h2>
          <p className="text-gray-600 mb-6">Please add items to your cart first</p>
          <button
            onClick={() => navigate('/')}
            className="bg-teal-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Go to Order Page
          </button>
        </div>
      </div>
    );
  }

  const isBusy = isProcessing || isPolling || isManuallyConfirming;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-4xl mx-auto px-4 py-6">
        {showSuccess ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-check-line text-4xl text-green-600 w-12 h-12 flex items-center justify-center"></i>
            </div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Payment Confirmed</h2>
            <p className="text-gray-600 mb-2">{successOrderNumber}</p>
            {receiptNumber && <p className="text-gray-600 mb-2">Receipt: {receiptNumber}</p>}
            <p className="text-gray-600 mb-4">Redirecting to live orders...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Customer Information</h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
                    {error}
                  </div>
                )}

                {(customerMessage || statusMessage || checkoutRequestId) && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm text-emerald-800">
                    <p className="font-medium mb-1">M-Pesa checkout in progress</p>
                    {customerMessage && <p>{customerMessage}</p>}
                    {statusMessage && <p className="mt-1">{statusMessage}</p>}
                    {checkoutRequestId && (
                      <p className="mt-2 text-xs text-emerald-700">Checkout ID: {checkoutRequestId}</p>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Customer Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Enter customer name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    required
                    disabled={isBusy}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number (M-Pesa) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    placeholder="07XXXXXXXX"
                    pattern="[0-9+ ]{10,15}"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    required
                    disabled={isBusy}
                  />
                  <p className="text-xs text-gray-500 mt-1">M-Pesa payment will be sent to this number</p>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <i className="ri-information-line text-amber-600 text-xl w-5 h-5 flex items-center justify-center mt-0.5"></i>
                    <div>
                      <h4 className="text-sm font-medium text-amber-900 mb-1">Payment Instructions</h4>
                      <p className="text-xs text-amber-800">
                        Enter the customer&apos;s M-Pesa number, then approve the STK prompt on their phone.
                      </p>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isBusy}
                  className={`w-full py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                    isBusy
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : 'bg-teal-500 text-white hover:bg-teal-600'
                  }`}
                >
                  {isBusy ? (
                    <span className="flex items-center justify-center">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      {isProcessing ? 'Sending STK Push...' : 'Waiting for Confirmation...'}
                    </span>
                  ) : (
                    'Send M-Pesa Prompt'
                  )}
                </button>

                {pendingOrderId !== null && (
                  <button
                    type="button"
                    onClick={handleManualConfirmation}
                    disabled={isProcessing || isManuallyConfirming}
                    className={`w-full py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                      isProcessing || isManuallyConfirming
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-amber-500 text-white hover:bg-amber-600'
                    }`}
                  >
                    {isManuallyConfirming ? 'Approving Payment...' : 'Confirm Payment Manually'}
                  </button>
                )}
              </form>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Order Summary</h2>

              <div className="space-y-3 mb-6">
                {cart.map((item) => (
                  <div key={item.id} className="flex justify-between items-center py-3 border-b border-gray-100">
                    <div className="flex-1">
                      <h4 className="font-medium text-sm text-gray-900">{item.name}</h4>
                      <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                    </div>
                    <p className="font-semibold text-sm text-gray-900">
                      KSh {(item.price * item.quantity).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-200 pt-4 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Subtotal</span>
                  <span className="text-sm font-medium">KSh {total.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Tax</span>
                  <span className="text-sm font-medium">KSh 0</span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                  <span className="text-base font-semibold text-gray-900">Total Amount</span>
                  <span className="text-2xl font-bold text-teal-600">KSh {total.toLocaleString()}</span>
                </div>
              </div>

              <div className="mt-6 bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Payment Method</h4>
                <div className="flex items-center space-x-2">
                  <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-xs">M-PESA</span>
                  </div>
                  <span className="text-sm text-gray-700">M-Pesa Mobile Payment</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
